// HomePage.js

import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Fade,
  List,
  ListItem,
  Paper
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  IoTrendingUpOutline,
  IoSparklesOutline
} from 'react-icons/io5';
import { MdFiberNew } from 'react-icons/md';

import LooksPost             from './LooksPost';
import { LooksPostsContext } from '../contexts/LooksPostsContext';
import { useAuth }           from '../contexts/AuthContext';
import VisibilityTracker     from './VisibilityTracker';

import './HomePage.css';

/* ─────────────────────────────────────────────
   LooksAway Home Feed — posts + Discover box
───────────────────────────────────────────── */
const MAX_RAW = 120;

const HomePage = () => {
  /* ---------- context ---------- */
  const {
    posts,
    fetchMorePosts,
    loading: contextLoading,
    hasMore,
    deletePostById
  } = useContext(LooksPostsContext);

  const { user: authUser } = useAuth();
  const navigate           = useNavigate();

  /* ---------- local state ---------- */
  const [clickedPosts, setClickedPosts] = useState(() => {
    const saved = localStorage.getItem('clickedPosts');
    return saved ? JSON.parse(saved) : {};
  });

  /* ---------- totals ---------- */
  const totalImpressions = useMemo(
    () =>
      posts.reduce(
        (sum, p) =>
          sum +
          (p.impressionCount             || 0) +
          (p.feedImpressionCount         || 0) +
          (p.discoveryBoxImpressionCount || 0) +
          (p.pageViewCount               || 0) +
          (p.viewerImpressions           || 0),
        0
      ),
    [posts]
  );

  /* ---------- Discover‑Posts refresh ---------- */
  const [randomPostsKey, setRandomPostsKey] = useState(0);
  const [isInitialLoad, setIsInitialLoad]   = useState(true);

  /* ---------- responsive ---------- */
  const isSmallScreen = useMediaQuery('(max-width:1180px)');

  /* ---------- shared CTA‑chip style (mobile) ---------- */
  const ctaButtonStyle = {
    display:         'flex',
    alignItems:      'center',
    gap:             '4px',
    px:              '12px',
    py:              '6px',
    fontSize:        '0.95rem',
    fontWeight:      500,
    color:           '#1a0dab',
    border:          '2px solid #007aff',
    borderRadius:    '8px',
    backgroundColor: '#f5f5f7',
    cursor:          'pointer',
    transition:      'background-color .25s',
    '&:hover':       { backgroundColor:'#e8e8ed' }
  };

  const postContainerStyle = {
    width:'100%',
    maxWidth:'800px',
    margin:'0 auto 15px',
    padding:0,
    boxSizing:'border-box'
  };

  /* ---------- helpers ---------- */
  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try   { await deletePostById(postId); }
    catch (err) { console.error(err); }
  };

  useEffect(() => window.scrollTo(0, 0), [authUser]);

  const homePagePosts = useMemo(() => {
    const valid = posts.filter(
      (p) => p.showOnHomePage && !p.deleted && !p.isReported
    );
    valid.sort((a, b) => {
      const aT = a.timestamp?.toDate
        ? a.timestamp.toDate().getTime()
        : new Date(a.timestamp || 0).getTime();
      const bT = b.timestamp?.toDate
        ? b.timestamp.toDate().getTime()
        : new Date(b.timestamp || 0).getTime();
      return bT - aT;
    });
    return valid;
  }, [posts]);

  const discoverPosts = useMemo(
    () =>
      posts.filter(
        (p) =>
          !p.deleted &&
          !p.isReported &&
          (p.showInDiscoverBox === true ||
            (p.showInDiscoverBox === undefined && p.showOnHomePage))
      ),
    [posts]
  );

  useEffect(() => {
    if (discoverPosts.length && isInitialLoad) setIsInitialLoad(false);
  }, [discoverPosts, isInitialLoad]);

  const randomDiscoverPosts = useMemo(() => {
    if (!discoverPosts.length || isInitialLoad) return [];
    const shuffled = [...discoverPosts].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(5, shuffled.length));
  }, [discoverPosts, randomPostsKey, isInitialLoad]);

  const handlePostClick = useCallback(
    (postId) => {
      const updated = { ...clickedPosts, [postId]: true };
      setClickedPosts(updated);
      localStorage.setItem('clickedPosts', JSON.stringify(updated));
      navigate(`/looksposts/${postId}`);
    },
    [clickedPosts, navigate]
  );

  const getLocationString = useCallback((city, state) => {
    if (city && state) return `(${city}, ${state})`;
    if (city)          return `(${city})`;
    if (state)         return `(${state})`;
    return '';
  }, []);

  const handleRefresh = useCallback(() => {
    setRandomPostsKey((prev) => prev + 1);
  }, []);

  /* ---------- loading / empty ---------- */
  if (contextLoading) {
    return (
      <Box sx={{ height:'50vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!posts.length) {
    return (
      <Typography variant="body1" sx={{ textAlign:'center', mt:3 }}>
        No posts found yet.
      </Typography>
    );
  }

  /* ---------- render ---------- */
  return (
    <>
      {/* extra top‑padding for phones/tablets so content clears the fixed Navbar */}
      <Box sx={{ pt:{ xs:14, md:10 }, pb:4 }}>
        <div className="posts">
          {homePagePosts.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign:'center', mb:3 }}>
              No home page posts yet.
            </Typography>
          ) : (
            <>
              {/* ========== Discover Posts Box ========== */}
              <Fade in timeout={800} unmountOnExit>
                <Paper
                  elevation={3}
                  sx={{
                    position:'relative',
                    display:'flex',
                    flexDirection:'column',
                    alignItems:'center',
                    p:4,
                    mt:0,
                    width:'100%',
                    maxWidth:800,
                    margin:'0 auto 30px',
                    borderRadius:'16px',
                    backgroundColor:'#fff',
                    overflow:'hidden'
                  }}
                >
                  {/* ---------- header ---------- */}
                  <Box sx={{ width:'100%', mb:2, textAlign:'left' }}>
                    {/* desktop / mobile header identical to your updated version */}
                    {!isSmallScreen ? (
                      <>
                        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <Typography variant="h4" sx={{ fontWeight:700, color:'#1d1d1f', lineHeight:1.2 }}>
                            Looksaway (Beta)
                          </Typography>

                          <IconButton
                            onClick={handleRefresh}
                            sx={{
                              color:'#007aff',
                              p:1,
                              backgroundColor:'#f5f5f7',
                              '&:hover':{ backgroundColor:'#e8e8ed' }
                            }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Box>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontSize:'1.15rem',
                            fontWeight:500,
                            color:'#3c3c43',
                            mt:0.5,
                            display:'flex',
                            alignItems:'center',
                            gap:0.5
                          }}
                        >
                          <IoSparklesOutline style={{ fontSize:18 }} />
                          Share & Explore Personal Bios
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontSize:'1.05rem', color:'#86868b', mt:0.5 }}
                        >
                          Discover & Reach New People
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight:700, color:'#1d1d1f', lineHeight:1.25 }}
                        >
                          Looksaway (Beta)
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize:'1.05rem',
                            fontWeight:500,
                            color:'#3c3c43',
                            mt:0.5,
                            display:'flex',
                            alignItems:'center',
                            gap:0.5
                          }}
                        >
                          <IoSparklesOutline style={{ fontSize:16 }} />
                          Share & Explore Personal Bios
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize:'0.95rem', color:'#86868b', mt:0.5 }}
                        >
                          Discover & Reach New People
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* ---------- list ---------- */}
                  <List disablePadding sx={{ width:'100%', p:0 }}>
                    {randomDiscoverPosts.map((post, index) => {
                      const raw      = (post.description ?? '').trim();
                      const teaser   =
                        raw.length <= MAX_RAW
                          ? raw
                          : post.summary ?? `${raw.slice(0, MAX_RAW)}…`;
                      const userName = post.userName || 'Anonymous';

                      return (
                        <VisibilityTracker key={post.id} postId={post.id} isDiscoveryBox>
                          <Fade in timeout={500 + index * 100} unmountOnExit>
                            <ListItem
                              disableGutters
                              component="div"
                              sx={{
                                display:'flex',
                                flexDirection:'column',
                                alignItems:'flex-start',
                                py:2,
                                pl:0,
                                pr:0,
                                borderRadius:2,
                                '&:hover':{ backgroundColor:'transparent' }
                              }}
                            >
                              <Box sx={{ display:'flex', flexDirection:'column', width:'100%', mb:1 }}>
                                <Typography
                                  variant="body1"
                                  component="span"
                                  onClick={() => handlePostClick(post.id)}
                                  sx={{
                                    color: clickedPosts[post.id] ? '#551A8B' : '#1a0dab',
                                    fontWeight:'bold',
                                    cursor:'pointer',
                                    textDecoration:'none',
                                    '&:hover':{ textDecoration:'none' },
                                    fontSize:'1.1rem',
                                    mb:'4px'
                                  }}
                                >
                                  {userName}
                                </Typography>

                                <Typography variant="body2" sx={{ color:'#86868b', fontSize:'0.9rem' }}>
                                  {getLocationString(post.city, post.state)}
                                </Typography>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{ color:'#1d1d1f', fontWeight:400, fontSize:'0.95rem' }}
                              >
                                {teaser}
                              </Typography>
                            </ListItem>
                          </Fade>
                        </VisibilityTracker>
                      );
                    })}
                  </List>

                  {/* ---------- refresh (small only) ---------- */}
                  {isSmallScreen && (
                    <Box sx={{ display:'flex', justifyContent:'center', mt:2 }}>
                      <IconButton
                        onClick={handleRefresh}
                        sx={{
                          color:'#007aff',
                          p:2,
                          backgroundColor:'#f5f5f7',
                          '&:hover':{ backgroundColor:'#e8e8ed' }
                        }}
                      >
                        <RefreshIcon sx={{ fontSize:48 }} />
                      </IconButton>
                    </Box>
                  )}

                  {/* ---------- totals + CTAs ---------- */}
                  {isSmallScreen ? (
                    /* ------- mobile / tablet row of chips ------- */
                    <Box
                      sx={{
                        display:'flex',
                        alignItems:'center',
                        justifyContent:'center',
                        flexWrap:'wrap',
                        gap:1.5,
                        mt:4
                      }}
                    >
                      {/* total views pill */}
                      <Typography
                        variant="body1"
                        sx={{ color:'#333', fontWeight:'bold', display:'flex', alignItems:'center' }}
                      >
                        <span
                          style={{
                            backgroundColor:'#fff',
                            color:'#000',
                            fontFamily:'Bebas Neue, sans-serif',
                            fontSize:'1.5rem',
                            padding:'5px 10px',
                            border:'2px solid #000',
                            borderRadius:'8px',
                            marginRight:'10px'
                          }}
                        >
                          {totalImpressions.toLocaleString()}
                        </span>
                        Total Post Views
                      </Typography>

                      {/* Trending Posts chip */}
                      <Box
                        sx={ctaButtonStyle}
                        onClick={() => navigate('/trending')}
                      >
                        <IoTrendingUpOutline size={20} />
                        <span>Trending&nbsp;Posts</span>
                      </Box>

                      {/* New Posts chip */}
                      <Box
                        sx={ctaButtonStyle}
                        onClick={() => navigate('/newposts')}
                      >
                        <MdFiberNew size={22} />
                        <span>New&nbsp;Posts</span>
                      </Box>
                    </Box>
                  ) : (
                    /* ------- desktop column (original look) ------- */
                    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', mt:4 }}>
                      <Typography
                        variant="body1"
                        sx={{ color:'#333', fontWeight:'bold', display:'flex', alignItems:'center' }}
                      >
                        <span
                          style={{
                            backgroundColor:'#fff',
                            color:'#000',
                            fontFamily:'Bebas Neue, sans-serif',
                            fontSize:'1.5rem',
                            padding:'5px 10px',
                            border:'2px solid #000',
                            borderRadius:'8px',
                            marginRight:'10px'
                          }}
                        >
                          {totalImpressions.toLocaleString()}
                        </span>
                        Total Post Views
                      </Typography>

                      <Box
                        sx={{
                          display:'flex',
                          alignItems:'center',
                          justifyContent:'center',
                          mt:2,
                          p:'8px 16px',
                          borderRadius:2,
                          cursor:'pointer',
                          backgroundColor:'#f5f5f7',
                          border:'2px solid #007aff',
                          transition:'background-color .3s',
                          '&:hover':{ backgroundColor:'#e8e8ed' }
                        }}
                        onClick={() => navigate('/trending')}
                      >
                        <Box sx={{ display:'flex', alignItems:'center', gap:1, color:'#1a0dab' }}>
                          <IoTrendingUpOutline size={24} />
                          <Typography variant="body1" sx={{ fontWeight:500, color:'#1a0dab' }}>
                            View Trending Posts
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Fade>

              {/* ===== Home‑feed posts ===== */}
              {homePagePosts.map((p) => (
                <div style={postContainerStyle} key={p.id}>
                  <LooksPost {...p} onDelete={() => handleDelete(p.id)} />
                </div>
              ))}
            </>
          )}
        </div>

        {/* ---------- infinite scroll ---------- */}
        {hasMore && (
          <Box sx={{ textAlign:'center', mt:4 }}>
            <button
              onClick={fetchMorePosts}
              className="load-more-btn"
              type="button"
            >
              Load&nbsp;More
            </button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default HomePage;

